/**
 * React Query hooks for Campuses API
 */

import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { campusesApi } from "@/api/campuses";
import type {
  Campus,
  ListCampusesParams,
  CreateCampusRequest,
  UpdateCampusRequest,
} from "@/types/campus";

/**
 * Query keys for Campuses
 */
export const campusesKeys = {
  all: ["campuses"] as const,
  lists: () => [...campusesKeys.all, "list"] as const,
  list: (params?: ListCampusesParams) =>
    [...campusesKeys.lists(), params] as const,
  details: () => [...campusesKeys.all, "detail"] as const,
  detail: (id: string) => [...campusesKeys.details(), id] as const,
};

/**
 * Hook to list campuses with filters
 */
export const useCampuses = (params?: ListCampusesParams) => {
  return useQuery({
    queryKey: campusesKeys.list(params),
    queryFn: async () => {
      const response = await campusesApi.list(params);
      return response.data;
    },
  });
};

/** campus_id -> name, for every campus regardless of status. */
export const buildCampusNameMap = (
  campuses: readonly Pick<Campus, "campus_id" | "name">[] | undefined,
): Map<string, string> => new Map((campuses ?? []).map((c) => [c.campus_id, c.name]));

/**
 * campus_id -> name for the whole tenant (archived campuses included, so a
 * class at a since-archived campus still gets its real name). The classes
 * API returns campus_name itself since 2026-10-29; this map serves the
 * pages that still look it up, and older API builds.
 */
export const useCampusNameMap = (): Map<string, string> => {
  const { data } = useCampuses();
  return useMemo(() => buildCampusNameMap(data?.campuses), [data?.campuses]);
};

/**
 * Hook to list public campuses (no authentication required)
 * For use on the public application form
 */
export const usePublicCampuses = (tenantId: string) => {
  return useQuery({
    queryKey: ["public-campuses", tenantId],
    queryFn: async () => {
      const response = await campusesApi.listPublic(tenantId);
      return response.data;
    },
    enabled: !!tenantId,
  });
};

/**
 * Hook to get campus details by ID
 */
export const useCampus = (id: string) => {
  return useQuery({
    queryKey: campusesKeys.detail(id),
    queryFn: async () => {
      const response = await campusesApi.get(id);
      return response.data;
    },
    enabled: !!id,
  });
};

/**
 * Hook to create a new campus
 */
export const useCreateCampus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCampusRequest) => {
      const response = await campusesApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campusesKeys.lists() });
      toast.success("Campus created successfully");
    },
    // Error handling done in form component via FormErrorAlert
  });
};

/**
 * Hook to update a campus
 */
export const useUpdateCampus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateCampusRequest;
    }) => {
      const response = await campusesApi.update(id, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: campusesKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: campusesKeys.detail(variables.id),
      });
      toast.success("Campus updated successfully");
    },
    // Error handling done in form component via FormErrorAlert
  });
};

/**
 * Hook to delete (archive) a campus
 */
export const useDeleteCampus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await campusesApi.delete(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campusesKeys.lists() });
      toast.success("Campus archived successfully");
    },
    // Error handling done in form component via FormErrorAlert
  });
};
